// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Checkbox, FormControlLabel } from '@mui/material';
import { documentsApi } from '@shared/data-access/api/engine/documents-api';
import type { RootDocumentsRepositoryState } from '@shared/data-access/interfaces/redux/documents-repository-state-types';
import {
  selectDocumentsForClientScope,
  selectDocumentsLinkedToEntityForClient,
} from '@shared/data-access/slices/documents-repository';
import type {
  Document,
  DocumentEntityType,
} from '@shared/data-access/types/engine-types';
import { ConfirmPopup, useDynamicDialog } from '@shared/ui-kit/components';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import { produce } from 'immer';
import { useCallback, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { isClientApp } from '@shared/ui-kit';
import { useDocuments } from '../../hooks/use-documents';
import { useDocumentsClientId } from '../../hooks/use-documents-client-id';
import { downloadDocumentFile } from '../../utils/download-document-files';
import {
  buildLinkedDocumentFileRow,
  filterRelatableDocuments,
  RELATABLE_DOCUMENTS_SEARCH_THRESHOLD,
  type LinkedDocumentFileRow,
} from '../entity-documents-section/entity-documents-section.utils';
import { filterLinkedDocuments } from './entity-documents-tab.utils';

export interface EntityDocumentsTabProps {
  readonly entityType: DocumentEntityType;
  readonly entityId: string;
  readonly disabled?: boolean;
}

type DocsTabUiState = {
  searchQuery: string;
  isUploadOpen: boolean;
  downloadingRowKey: string | null;
  linkingDocumentId: number | null;
  relatableSearchQuery: string;
};

const initialUiState: DocsTabUiState = {
  searchQuery: '',
  isUploadOpen: false,
  downloadingRowKey: null,
  linkingDocumentId: null,
  relatableSearchQuery: '',
};

export const useEntityDocumentsTab = ({
  entityType,
  entityId,
  disabled = false,
}: EntityDocumentsTabProps) => {
  const { t } = useTranslation();
  const clientId = useDocumentsClientId();
  const { documentsQuery } = useDocuments();
  const { openDialog, closeDialog } = useDynamicDialog();
  const { enqueueSnackbar } = useSnackbar();

  const [ui, setUi] = useState<DocsTabUiState>(initialUiState);

  const [updateDocument, updateDocumentState] =
    documentsApi.useUpdateDocumentMutation();
  const [deleteDocument] = documentsApi.useDeleteDocumentMutation();
  const [fetchDocumentDetail] = documentsApi.useFetchDocumentDetailMutation();

  const linkedDocuments = useSelector((state: RootDocumentsRepositoryState) =>
    selectDocumentsLinkedToEntityForClient(state, clientId, entityType, entityId),
  );

  const clientDocuments = useSelector((state: RootDocumentsRepositoryState) =>
    selectDocumentsForClientScope(state, clientId),
  );

  const safeLinkedDocuments = useMemo(
    () => (Array.isArray(linkedDocuments) ? linkedDocuments : []),
    [linkedDocuments],
  );

  const safeClientDocuments = useMemo(
    () => (Array.isArray(clientDocuments) ? clientDocuments : []),
    [clientDocuments],
  );

  const linkedDocumentIds = useMemo(
    () => new Set(safeLinkedDocuments.map(document => document.id)),
    [safeLinkedDocuments],
  );

  const relatableDocuments = useMemo(
    () =>
      safeClientDocuments.filter(document => !linkedDocumentIds.has(document.id)),
    [safeClientDocuments, linkedDocumentIds],
  );

  const filteredRelatableDocuments = useMemo(
    () => filterRelatableDocuments(relatableDocuments, ui.relatableSearchQuery),
    [relatableDocuments, ui.relatableSearchQuery],
  );

  const filteredLinkedDocuments = useMemo(
    () => filterLinkedDocuments(safeLinkedDocuments, ui.searchQuery),
    [safeLinkedDocuments, ui.searchQuery],
  );

  const hasEntityId = entityId !== '';
  const isCatalogLoading =
    documentsQuery.isLoading && safeClientDocuments.length === 0;
  const hasLinkedDocuments = safeLinkedDocuments.length > 0;
  const hasFilteredResults = filteredLinkedDocuments.length > 0;
  const showEmptyNoDocuments = !isCatalogLoading && !hasLinkedDocuments;
  const showEmptyNoResults =
    !isCatalogLoading && hasLinkedDocuments && !hasFilteredResults;
  const hasRelatableDocuments = relatableDocuments.length > 0;
  const showRelatableSearch =
    relatableDocuments.length > RELATABLE_DOCUMENTS_SEARCH_THRESHOLD;
  const canManageDocuments = isClientApp && !disabled && hasEntityId;
  const showLinkPanel = canManageDocuments;
  const showUnlinkAction = canManageDocuments;
  const canUpload = canManageDocuments;

  const onSearchQueryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const searchQuery = event.target.value;

      setUi(prev =>
        produce(prev, draft => {
          draft.searchQuery = searchQuery;
        }),
      );
    },
    [],
  );

  const onRelatableSearchQueryChange = useCallback(
    (relatableSearchQuery: string) => {
      setUi(prev =>
        produce(prev, draft => {
          draft.relatableSearchQuery = relatableSearchQuery;
        }),
      );
    },
    [],
  );

  const onOpenUpload = useCallback(() => {
    if (!canUpload) {
      return;
    }

    setUi(prev =>
      produce(prev, draft => {
        draft.isUploadOpen = true;
      }),
    );
  }, [canUpload]);

  const onCloseUpload = useCallback(() => {
    setUi(prev =>
      produce(prev, draft => {
        draft.isUploadOpen = false;
      }),
    );
  }, []);

  const onLinkDocument = useCallback(
    async (documentId: number) => {
      if (
        !documentId ||
        !showLinkPanel ||
        ui.linkingDocumentId !== null ||
        updateDocumentState.isLoading
      ) {
        return;
      }

      setUi(prev =>
        produce(prev, draft => {
          draft.linkingDocumentId = documentId;
        }),
      );

      try {
        await updateDocument({
          document_id: documentId,
          body: {
            relations_to_add: [
              {
                entity_type: entityType,
                entity_id: entityId,
              },
            ],
          },
        }).unwrap();
      } catch {
        enqueueSnackbar(
          t('documents.entity-section.link-error', { ns: 'components' }),
          { variant: 'error' },
        );
      } finally {
        setUi(prev =>
          produce(prev, draft => {
            draft.linkingDocumentId = null;
          }),
        );
      }
    },
    [
      enqueueSnackbar,
      entityId,
      entityType,
      showLinkPanel,
      t,
      ui.linkingDocumentId,
      updateDocument,
      updateDocumentState.isLoading,
    ],
  );

  const onUnlinkDocument = useCallback(
    (document: Document) => {
      if (
        ui.linkingDocumentId !== null ||
        updateDocumentState.isLoading ||
        !showUnlinkAction
      ) {
        return;
      }

      const UnlinkDocumentConfirmDialog = () => {
        const [forceDelete, setForceDelete] = useState(false);
        const [isLoading, setIsLoading] = useState(false);

        const handleConfirm = async () => {
          setIsLoading(true);

          try {
            if (forceDelete) {
              await deleteDocument({
                document_id: document.id,
                force: true,
              }).unwrap();
            } else {
              await updateDocument({
                document_id: document.id,
                body: {
                  relations_to_remove: [
                    {
                      entity_type: entityType,
                      entity_id: entityId,
                    },
                  ],
                },
              }).unwrap();
            }

            closeDialog();
          } catch {
            enqueueSnackbar(
              forceDelete
                ? t('documents.detail-drawer.delete-error', { ns: 'components' })
                : t('documents.entity-section.unlink-error', {
                    ns: 'components',
                  }),
              { variant: 'error' },
            );
            closeDialog();
          } finally {
            setIsLoading(false);
          }
        };

        return (
          <ConfirmPopup
            title={t('documents.entity-section.unlink-confirm.title', {
              ns: 'components',
            })}
            message={t('documents.entity-section.unlink-confirm.message', {
              ns: 'components',
              title: document.title,
            })}
            confirmText={t('documents.entity-section.unlink', {
              ns: 'components',
            })}
            onConfirm={handleConfirm}
            onCancel={closeDialog}
            isLoading={isLoading}
            icon="Trashcan"
          >
            <FormControlLabel
              className="!mx-0 !mt-4 items-start"
              control={
                <Checkbox
                  checked={forceDelete}
                  onChange={event => setForceDelete(event.target.checked)}
                  data-testid="entity-docs-unlink-force-checkbox"
                />
              }
              label={
                <span className="text-left text-sm text-primary-medium">
                  {t('documents.detail-drawer.delete-force-label', {
                    ns: 'components',
                  })}
                </span>
              }
            />
          </ConfirmPopup>
        );
      };

      openDialog(<UnlinkDocumentConfirmDialog />, {
        showCloseButton: false,
        classes: { paper: '!w-[335px] !overflow-y-visible' },
      });
    },
    [
      closeDialog,
      deleteDocument,
      enqueueSnackbar,
      entityId,
      entityType,
      openDialog,
      showUnlinkAction,
      t,
      ui.linkingDocumentId,
      updateDocument,
      updateDocumentState.isLoading,
    ],
  );

  const onDownloadFile = useCallback(
    async (row: LinkedDocumentFileRow) => {
      setUi(prev =>
        produce(prev, draft => {
          draft.downloadingRowKey = row.rowKey;
        }),
      );

      try {
        const detail = await fetchDocumentDetail({
          document_id: row.documentId,
        }).unwrap();

        const file = detail.files.find(
          detailFile => detailFile.file_name === row.fileName,
        );

        if (file?.url) {
          downloadDocumentFile(file.url, file.file_name);
        }
      } finally {
        setUi(prev =>
          produce(prev, draft => {
            draft.downloadingRowKey = null;
          }),
        );
      }
    },
    [fetchDocumentDetail],
  );

  const buildFileRowsForDocument = useCallback(
    (document: Document): LinkedDocumentFileRow[] =>
      (document.files ?? []).map((file, fileIndex) =>
        buildLinkedDocumentFileRow(document, file, fileIndex),
      ),
    [],
  );

  return {
    t,
    entityType,
    entityId,
    linkedCount: safeLinkedDocuments.length,
    linkedDocuments: filteredLinkedDocuments,
    searchQuery: ui.searchQuery,
    isUploadOpen: ui.isUploadOpen,
    downloadingRowKey: ui.downloadingRowKey,
    linkingDocumentId: ui.linkingDocumentId,
    isCatalogLoading,
    showEmptyNoDocuments,
    showEmptyNoResults,
    canUpload,
    showUnlinkAction,
    showLinkPanel,
    hasRelatableDocuments,
    showRelatableSearch,
    relatableSearchQuery: ui.relatableSearchQuery,
    relatableDocuments: filteredRelatableDocuments,
    onSearchQueryChange,
    onRelatableSearchQueryChange,
    onOpenUpload,
    onCloseUpload,
    onLinkDocument,
    onUnlinkDocument,
    onDownloadFile,
    buildFileRowsForDocument,
  };
};
